import React, { useCallback, useEffect, useState } from 'react';
import { ButtonToolbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Measure from 'react-measure';
import {
  useBlockLayout,
  useExpanded,
  useFilters,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table';
import { useSticky } from 'react-table-sticky';
import { Scroll } from 'scroll-utility';

import { uiConstants } from '../../../constants';
import { generalHelpers } from '../../../helpers';
import { ReactTable } from '../../../types';
import LoadingImage from '../../general/LoadingImage';
import ShortkeyListener from '../../shortkey/ShortkeyListener';
import TableDataRow from '../DLTableRow';
import {
  numberFilterMethod,
  rangeFilterMethod,
  stringFilterMethod,
} from '../filter-methods';
import { NumberFilter } from '../filters';
import ColumnWidthManager from './ColumnWidthManager';
import { ExportButton } from './ExportButton';
import { PaginationButtons } from './PaginationButtons';
import { Styles } from './styles';
import { TableHeaders } from './TableHeaders';

export const CANVAS_CELL_CLASS_NAME = 'canvas-cell';
export const DESKTOP_ONLY_CLASS_NAME = 'd-none d-md-block';

const useExpandedLegacy = hooks => {
  hooks.stateReducers.push(function reducer(state, action) {
    if (action.type === 'setExpanded') {
      return {
        ...state,
        expanded: { ...action.expanded },
      };
    }

    return state;
  });
};

interface Props {
  tableId: string;
  columns: ReactTable.Column[];
  data: ReadonlyArray<any>;
  loading?: boolean;
  showAllRows?: boolean;
  renderRowSubComponent?: (params: any) => React.ReactNode;
  defaultSortBy?: any[];
  defaultFiltered?: any[];
  defaultPageSize?: number;
  allowExport?: boolean;
  exportFileName?: string;
  onFiltersChanged?: (filters: any[]) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const DLTable: React.FC<Props> = function DLTable({
  tableId,
  columns,
  data,
  loading = false,
  showAllRows = false,
  renderRowSubComponent = () => null,
  defaultSortBy = [],
  defaultFiltered = [],
  defaultPageSize = 50,
  allowExport = false,
  exportFileName = 'datalouder-export',
  onFiltersChanged = generalHelpers.noop,
}) {
  const [width, setWidth] = useState(800);
  const filterTypes = React.useMemo(
    () => ({
      // "startWith"
      number: numberFilterMethod,
      text: stringFilterMethod,
      range: rangeFilterMethod,
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: NumberFilter,
      filter: 'number',
      sortType: 'basic',
    }),
    []
  );

  const [columnWidthManager] = useState(
    new ColumnWidthManager(
      tableId,
      columns.map(({ id }) => id)
    )
  );

  useEffect(() => {
    columnWidthManager.updateDefaultWidths(columns);
    columnWidthManager.updateVisibleColumnIds(columns.map(({ id }) => id));
  }, [columnWidthManager, columns]);

  const columnsWithWidthAdjusted = React.useMemo(
    () => columnWidthManager.applyColumnWidths(width, columns),
    [columnWidthManager, width, columns]
  );

  const {
    dispatch,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // pagination
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, expanded, filters },
  } = useTable(
    {
      columns: columnsWithWidthAdjusted,
      data,
      defaultColumn,
      filterTypes,
      initialState: {
        pageIndex: 0,
        pageSize: defaultPageSize,
        sortBy: defaultSortBy,
        filters: defaultFiltered,
      },

      autoResetPage: false,
      autoResetExpanded: false,
      autoResetGroupBy: false,
      autoResetSelectedRows: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetRowState: false,
    },
    useBlockLayout,

    useResizeColumns,
    useSticky,
    useFilters,
    useSortBy,
    useExpanded,
    useExpandedLegacy,
    usePagination
  );

  const { t } = useTranslation();

  const showPagination = !showAllRows;
  const visibleRows = showAllRows ? rows : page;

  const onArrowPressed = useCallback(
    key => {
      const indexs = Object.keys(expanded);
      const loc = indexs.length > 0 ? Number.parseInt(indexs[0]) : null;
      const mode =
        indexs.length > 0 ? expanded[loc] : uiConstants.MODE_DAILY_PRICE;
      let iLoc =
        visibleRows.map(x => x.index).indexOf(loc) +
        (key === 'up' ? -1 : 0) +
        (key === 'down' ? 1 : 0) +
        (key === 'shift+up' ? -5 : 0) +
        (key === 'shift+down' ? 5 : 0);
      if (visibleRows.length > 0) {
        if (iLoc < 0) {
          iLoc = 0;
        }
        if (iLoc > visibleRows.length - 1) {
          iLoc = visibleRows.length - 1;
        }
      }
      dispatch({
        type: 'setExpanded',
        expanded: { [visibleRows[iLoc].index]: mode },
      });
      setTimeout(() => {
        // make sure it is centered
        const expandedElement = document.querySelectorAll('.expanded')[0];
        if (expandedElement) {
          const scrollManager = new Scroll();
          scrollManager.scrollTo.element(
            document.querySelectorAll('.expanded')[0],
            0.1,
            1000
          );
        }
      }, 200);
    },
    [dispatch, expanded, visibleRows]
  );

  // when filters value changes, upload it to the server
  useEffect(() => {
    onFiltersChanged(filters);
  }, [filters, onFiltersChanged]);

  const isResizing = headerGroups.some(headerGroup =>
    headerGroup.headers.some(column => {
      if (column.isResizing) {
        columnWidthManager.updateWidth(width, column.id, Number(column.width));
      }

      return column.isResizing;
    })
  );

  return (
    <div className="react-table">
      <Styles>
        {data && (
          <Measure
            bounds
            onResize={contentRect => {
              setWidth(contentRect.bounds.width);
            }}
          >
            {({ measureRef }) => (
              <div
                ref={measureRef}
                {...getTableProps()}
                className="react-table sticky"
                style={{ width: '100%' }}
              >
                <div className="header">
                  {headerGroups.map((headerGroup, i) => (
                    <TableHeaders headerGroup={headerGroup} key={i} />
                  ))}
                </div>
                {visibleRows.map((row, i) => {
                  row.rowIndex = i + 1 + pageSize * pageIndex;
                  prepareRow(row);
                  return (
                    <React.Fragment key={i}>
                      <div {...getTableBodyProps()} className="tr-container">
                        <TableDataRow
                          row={row}
                          isExpanded={row.isExpanded}
                          isResizing={isResizing}
                        />
                      </div>

                      {row.isExpanded ? (
                        <>
                          <div
                            style={{
                              width,
                              position: 'sticky',
                              left: 0,
                            }}
                          >
                            {renderRowSubComponent({
                              row,
                              mode: row.isExpanded,
                            })}
                          </div>
                        </>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </Measure>
        )}
        <LoadingImage displayAsBlock loading={loading} />
        <ShortkeyListener
          handleKeys={['up', 'down', 'shift+up', 'shift+down']}
          onKeyEvent={onArrowPressed}
          shortkeys={[
            {
              section: 'table',
              key: '↑',
              description: t('previous-stock'),
            },
            {
              section: 'table',
              key: '↓',
              description: t('next-stock'),
            },
            {
              section: 'table',
              key: 'shift+↑',
              description: t('previous-stock-5'),
            },
            {
              section: 'table',
              key: 'shift+↓',
              description: t('next-stock-5'),
            },
          ]}
        />
      </Styles>
      {(showPagination || allowExport) && (
        <ButtonToolbar
          style={{
            justifyContent: 'space-around',
            alignItems: 'center',
            overflow: 'hidden', // toolbar somehow has content overflowed
          }}
        >
          {showPagination && (
            <PaginationButtons
              gotoPage={gotoPage}
              previousPage={previousPage}
              canPreviousPage={canPreviousPage}
              nextPage={nextPage}
              canNextPage={canNextPage}
              pageCount={pageCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          )}
          {allowExport && (
            <ExportButton
              allowExport={allowExport}
              columns={columns}
              data={data}
              exportFileName={exportFileName}
            />
          )}
        </ButtonToolbar>
      )}
    </div>
  );
};

export default React.memo(DLTable);
